import { useEffect, useRef, useState } from 'react';
import { getFreshness } from '../api.js';

/**
 * Tell the page when the index has moved underneath it.
 *
 * A tab left open shows whatever the last search returned. That is fine for a
 * minute and wrong for an afternoon: the whole point of tightening the sync is
 * that a change in Mongo shows up here, and it does not show up in a window
 * nobody has touched since lunch.
 *
 * Polling, not SSE. The signal changes at most once per sync interval - a minute
 * or two - so a long-lived connection per tab, a server-side broadcast bus and
 * the reverse-proxy buffering config to keep it alive would all be paid for a
 * message that arrives twice an hour. Revisit if the sync becomes event-driven,
 * at which point there are real events to push.
 */
const POLL_MS = 30_000;
const IDLE_RETRY_MS = 2_000;
const NOTE_MS = 4_000;

/**
 * Is the user in the middle of something a refresh would disturb?
 *
 * Redrawing the rail replaces every checkbox, so a refresh landing while the
 * pointer is over it can remove the option being clicked - the click then lands
 * on whatever moved into that position, which is worse than slightly old counts.
 * Typing into the search box is the same problem for the same reason.
 */
function busy() {
  const rail = document.getElementById('rail');
  if (rail && rail.matches(':hover')) return true;

  const el = document.activeElement;
  if (!el) return false;
  if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') return true;
  return Boolean(rail && rail.contains(el));
}

/**
 * Poll `/api/<domain>/freshness` and call `onMoved` when the stamp changes.
 *
 * `onMoved` returns `true` if it refreshed the view and `false` if it declined -
 * a page paged deep enough that a silent rebuild would throw away what the user
 * loaded says so, and gets an offer instead of a surprise.
 *
 * Returns the note to render, or null. The note is state rather than a DOM node
 * appended by the hook, so React owns everything on the page.
 */
export function useFreshness(domain, onMoved) {
  const [note, setNote] = useState(null);

  // Held in a ref so a new `onMoved` identity on every render does not tear down
  // and restart the polling timers.
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;

  useEffect(() => {
    let stamp = null;
    let pending = false;
    let inFlight = false;
    let cancelled = false;
    let noteTimer;

    const announce = (text) => {
      setNote(text);
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => setNote(null), NOTE_MS);
    };

    async function apply() {
      pending = false;
      const refreshed = await onMovedRef.current();
      if (cancelled) return;
      announce(refreshed === false
        ? 'New data is available — search again to see it'
        : 'Updated with the latest data');
    }

    async function check() {
      // A backgrounded tab must not poll: dozens of idle tabs asking every 30
      // seconds is a load pattern nobody intended to create.
      if (document.visibilityState !== 'visible' || inFlight) return;
      inFlight = true;
      try {
        const { stamp: next } = await getFreshness(domain);
        if (cancelled) return;
        if (stamp === null) { stamp = next; return; }  // first response is the baseline
        if (next === stamp) return;
        stamp = next;
        pending = true;
      } catch {
        // Offline, or the server is restarting. Nothing to say - the next poll
        // picks it up, and an error banner for a background check the user did
        // not ask for is noise.
      } finally {
        inFlight = false;
      }
    }

    const poll = setInterval(check, POLL_MS);
    // Deferred work is retried on a short timer rather than through mouse and
    // focus listeners, which would have to be added, removed and reasoned about
    // across a rail that React replaces on every render.
    const retry = setInterval(() => { if (pending && !busy()) apply(); }, IDLE_RETRY_MS);

    // Coming back to the tab is the common case - someone returns after lunch
    // and should not wait another 30 seconds to see today's data.
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    check();

    return () => {
      cancelled = true;
      clearInterval(poll);
      clearInterval(retry);
      clearTimeout(noteTimer);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [domain]);

  return note;
}
